/* ============================================================================
   epg-designer-reel.js — the designer, turning, on a landing page.

   WHAT THIS IS FOR. The personalisation designer is the reason to buy from
   this shop rather than from a catalogue, and until these pages existed the
   only way to see it was to find a personalised product and open a modal.
   A landing page has to show the thing working, in motion, before anybody
   has clicked anything — so this stands a real garment on the page, wearing
   a real design, and turns it.

   IT IS THE SAME ENGINE, NOT A SECOND ONE. It loads and drives exactly the
   chain `snippets/personalisation-designer.liquid` drives —
   eps-garment-3d.js (the maths, the print panels, the baked-mesh decoder),
   eps-three.js (the vendored three.js) and eps-garment-three.js (the
   renderer) — and paints through the same `paint` callback against the same
   `GarmentSpin.PANELS`. So what a visitor sees on a landing page is the
   garment the designer would give them, not a mock-up of it. Change the
   meshes and these pages follow for free.

   FIVE THINGS THAT ARE LOAD-BEARING:

   - THE CHAIN WAITS FOR `load`, AND THEN FOR THE VIEWPORT. It is ~680KB
     across three files plus a 100–680KB mesh, and a page whose job is to
     rank cannot have that competing with its own first paint. Nothing is
     requested until `window.load` has fired AND an IntersectionObserver says
     the stage is within 400px, so the poster is what LCP measures and the
     engine is paid for out of idle time afterwards.

     ON THESE PAGES THE REEL IS IN THE HERO, so in practice the observer
     fires immediately and only the `load` gate is doing work — measured,
     `engineLoaded` is already true before any scroll. The margin earns its
     place if the block is ever dropped further down a page, which the
     heading-level setting explicitly allows.

   - AUTOPLAY RETIRES ON TOUCH, AND HAS A REAL STOP. WCAG 2.2.2 wants a
     mechanism to pause anything that moves for more than five seconds, and
     this moves for as long as the page is open. Dragging the garment retires
     the turn for the rest of the page load — the rule `assets/epg-rail-auto.js`
     records, for the same reason: handing a carousel back to itself after a
     few seconds is the behaviour carousels are disliked for. The button is
     the explicit control, and it says which state it is in rather than which
     state it would go to.

   - REDUCED MOTION IS RE-CHECKED LIVE, not read once at load. The reel still
     builds under it and still answers a drag; it simply parks at three
     quarters and does not turn itself. Turning the setting off starts it
     without a reload, which is `epg-rail-auto.js`'s rule again.

   - A BACKGROUNDED TAB IS SERVED NO FRAMES, so the rAF loop stops on its
     own. What does NOT stop on its own is the elapsed time: driving the yaw
     from a wall clock would bank the whole hidden interval and snap the
     garment round on return. The step is per-frame with the delta clamped to
     a third of a second, so a tab that comes back resumes where it left off.

   - FAILING IS SILENT AND COMPLETE. No WebGL, a 404 on any of the three
     scripts, an unparseable mesh: the canvas is removed and the poster it
     was standing over is what remains. A landing page must not show a hole
     where a garment was.

   The Browser pane cannot verify ANY of this — it does not composite, so
   IntersectionObserver never re-fires on scroll and a WebGL canvas
   screenshots blank. Drive real headless Chrome over CDP with
   `--use-gl=swiftshader --enable-unsafe-swiftshader`, in real time; under
   `--virtual-time-budget` a scroll never progresses and IO only ever fires
   its first record, which reads as a dead engine.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__epgDesignerReel) return;
  window.__epgDesignerReel = true;

  var TAU = Math.PI * 2;

  /* THE STAGE'S ASPECT IS THE BLOCK'S, NOT THIS FILE'S. It used to be a table
     here, written onto the CANVAS — while the stage, its PARENT, was the
     element reading it, and a custom property set on a child never reaches
     its parent. Every stage stayed square, so a three-piece kit, a pair of
     trousers and a landscape towel all drew into a 1:1 box.

     Writing it onto the root would have fixed that and left a worse fault:
     this file does not run until `load` and the observer have both fired, so
     the stage would have been square through first paint and then resized
     under the reader — a layout shift on a page built to rank. The block
     stamps it inline instead. Do not reintroduce a copy here. */

  /* Where the turn rests when it cannot run — under reduced motion, and the
     frame a paused reel stops on if it is paused before it has moved. Three
     quarters, because it is the only view that shows a garment has depth,
     and on headwear it is the only view that shows the peak at all: at yaw 0
     a cap's peak points straight down the camera and reads as a peakless
     hat. */
  var REST_YAW = -0.62;

  /* ---------------------------------------------------------------- utils */

  function reduced() {
    return !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* One fetch per URL per page, however many reels ask for it. Two reels on
     one page would otherwise append the whole chain twice and race each
     other to define the globals. */
  var loading = {};
  function loadScript(src) {
    if (!src) return Promise.reject(new Error('no src'));
    if (loading[src]) return loading[src];
    loading[src] = new Promise(function (res, rej) {
      var existing = document.querySelector('script[data-epg-reel-src="' + src + '"]');
      if (existing) {
        if (existing.dataset.epgReelDone) { res(); return; }
        existing.addEventListener('load', function () { res(); });
        existing.addEventListener('error', function () { rej(new Error(src)); });
        return;
      }
      var tag = document.createElement('script');
      tag.src = src;
      tag.async = false;            /* order matters — see the header note */
      tag.dataset.epgReelSrc = src;
      tag.addEventListener('load', function () {
        tag.dataset.epgReelDone = '1';
        res();
      });
      tag.addEventListener('error', function () { rej(new Error(src)); });
      document.head.appendChild(tag);
    });
    return loading[src];
  }

  function parseJSON(raw, fallback) {
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }

  /* ------------------------------------------------------------- the reel */

  function Reel(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-epg-reel-canvas]');
    this.button = root.querySelector('[data-epg-reel-toggle]');
    this.d = root.dataset;

    this.shape = this.d.shape || 'shirt';
    this.speed = (parseFloat(this.d.speed) || 9) / 100;   /* radians a second */
    this.marks = parseJSON(this.d.marks, []);
    this.meshes = parseJSON(this.d.meshes, {});

    this.spin = null;
    this.running = false;
    this.retired = false;          /* a drag retires the turn for good */
    this.visible = false;
    this.booted = false;
    this._raf = 0;
    this._last = 0;
    this._imgs = {};

    if (!this.canvas) return;
    /* A HANDLE FOR VERIFICATION, and it has to be here rather than in a test.
       A WebGL canvas does not preserve its drawing buffer, so `toDataURL` on
       it comes back blank and every automated check reads as a dead render.
       What IS readable is the print texture — a plain 2D canvas — and the
       yaw, and neither is reachable without a reference to the instance.
       This is the same reason the designer's own checks read the texture
       rather than the model. */
    root.__epgReel = this;
    this.bindButton();
    this.watch();
  }

  /* NOTHING LOADS UNTIL IT IS WORTH LOADING. `rootMargin` is generous so the
     chain has landed by the time the garment is actually looked at, and the
     observer disconnects on the first hit — this is a one-shot trigger, not
     a visibility tracker. Visibility is tracked separately below, and only
     once there is something to pause. */
  Reel.prototype.watch = function () {
    var self = this;
    /* AFTER `load`, NEVER BEFORE IT. `defer` keeps this file off the parser's
       path but says nothing about what it goes on to fetch, and the three
       scripts plus a mesh would otherwise contend with the poster — which is
       the image LCP is actually measuring on these pages. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { self.watch(); }, { once: true });
      return;
    }
    if (!window.IntersectionObserver) { this.boot(); return; }
    var io = new IntersectionObserver(function (rows) {
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].isIntersecting) { io.disconnect(); self.boot(); return; }
      }
    }, { rootMargin: '400px 0px' });
    io.observe(this.root);
  };

  Reel.prototype.boot = function () {
    if (this.booted) return;
    this.booted = true;
    var self = this;
    var d = this.d;

    loadScript(d.g3dSrc)
      .then(function () { return loadScript(d.g3dThree); })
      .then(function () { return loadScript(d.g3dRenderer); })
      .then(function () {
        if (!window.GarmentSpin || !window.GarmentThree) throw new Error('engine');
        self.build();
      })
      .catch(function () { self.fail(); });
  };

  /* The poster stays; the canvas goes. A landing page must not show a hole
     where a garment was, and the poster is already underneath it — it is
     what the page painted before any of this was asked for. */
  Reel.prototype.fail = function () {
    this.root.dataset.epgReelState = 'static';
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    if (this.button) this.button.hidden = true;
  };

  Reel.prototype.build = function () {
    var self = this;

    /* WAIT FOR THE AUTHORED MESH BEFORE SHOWING THE CANVAS — the opposite of
       the designer's rule, and deliberately.

       The designer must not wait: it is a tool, the panel has to answer the
       moment it is opened, and a garment that improves a second later is
       better than a second of nothing. A landing page is the other way
       round. The poster underneath is ALREADY this garment, baked, at this
       colour, wearing this design — so revealing early gains nothing and
       costs the one thing that was visible: the canvas is stacked ON TOP of
       the poster, so `GarmentThree`'s first frame painted the PROCEDURAL loft
       over a correct picture, and the baked mesh then replaced it. Reported
       as "these flash when the page loads and randomly white sometimes" —
       random because it is a download race against a 100-680KB asset, and
       pale because the runtime loft is a far cruder shape than the bake.

       So the canvas stays hidden until there is something worth showing.
       Nothing here can strand it: a shape with no mesh reveals at once, a
       mesh that fails reveals the procedural shape rather than nothing, and
       the timeout reveals regardless. */
    var url = this.meshes[this.shape];
    var needMesh = !!(url && window.GarmentSpin.loadMesh &&
                      !window.GarmentSpin.hasMesh(this.shape));

    this.spin = new window.GarmentThree(this.canvas, {
      garment: this.shape,
      body: this.d.body || '#242833',
      trim: this.d.trim || this.d.body || '#242833',
      paint: function (ctx, size, h, engine) { self.paint(ctx, size, engine); }
    });
    if (!this.spin.ok) { this.spin = null; this.fail(); return; }

    this.spin.yaw = REST_YAW;
    this.spin.start();

    /* The garment answers a drag whether or not it turns itself, and a drag
       is an explicit interaction: it retires the turn for the rest of the
       page load rather than handing it back after a few seconds. */
    this.spin.bindDrag();
    this.canvas.addEventListener('pointerdown', function () { self.retire(); });

    if (needMesh) {
      /* A poster left up for ever is worse than a crude mesh, so the reveal
         is also on a timer. Whichever arrives first wins; `reveal` is
         idempotent. */
      window.setTimeout(function () { self.reveal(); }, 8000);
      window.GarmentSpin.loadMesh(this.shape, url).then(function (ok) {
        if (ok && self.spin) { self.spin.refreshMesh(); self.spin.refreshTexture(); }
        self.reveal();
      })['catch'](function () { self.reveal(); });
    } else {
      this.reveal();
    }

    /* Re-checked live, not read once: turning the setting off starts the
       reel without a reload. */
    if (window.matchMedia) {
      var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      var onChange = function () { self.sync(); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    this.track();
    this.sync();
  };

  /* THE ONE PLACE THE CANVAS IS SHOWN. Called from several paths — the mesh
     landing, the mesh failing, a shape that has no mesh, and a timeout — so
     it has to be idempotent. Two frames are drawn rather than one: the first
     is the swap to the baked geometry, and `refreshTexture` has to have run
     against it before the marks sit where the poster put them. */
  Reel.prototype.reveal = function () {
    if (this._revealed || !this.spin) return;
    this._revealed = true;
    this.spin.draw();
    this.root.dataset.epgReelState = 'live';
    if (this.button) this.button.hidden = false;
    this.sync();
  };

  /* ---------------------------------------------------------- the carousel

     POINT AN ALREADY-BUILT REEL AT A DIFFERENT GARMENT. A landing page shows
     one garment for ever and has never needed this; a band that carousels
     through a polo, a hi-vis, a softshell and a mug needs four garments out
     of ONE WebGL context, because a browser reclaims contexts oldest-first
     past about four and a context per slide spends the whole budget on one
     section.

     `GarmentThree` already supports it — `garment` is a plain property and
     `refreshMesh` rebuilds the geometry AND re-frames the camera from the new
     mesh's own bounds — so this is a sequencing method, not new machinery.

     IT DOES NOT TOUCH THE MESH UNTIL THE BAKE IS IN HAND. Swapping `garment`
     and rebuilding immediately draws the PROCEDURAL loft — a far cruder shape
     than the bake — and then replaces it a beat later, which is the
     "flashes white on load" fault this file's `build` already records. So a
     shape whose bake has not arrived is downloaded first and applied once,
     and the caller is told when by the promise. Callers that want the swap
     to be instant should `prefetch` during the previous slide's dwell.

     The colours and the marks ride along because all three change together:
     a garment, what it is printed with, and what colour it is printed on are
     one slide, and applying them in three calls draws two wrong frames. */
  Reel.prototype.prefetch = function (shape) {
    var url = this.meshes[shape];
    if (!url || !window.GarmentSpin || !window.GarmentSpin.loadMesh) {
      return Promise.resolve(false);
    }
    if (window.GarmentSpin.hasMesh(shape)) return Promise.resolve(true);
    return window.GarmentSpin.loadMesh(shape, url)['catch'](function () { return false; });
  };

  Reel.prototype.setSlide = function (o) {
    var self = this;
    o = o || {};
    if (!this.spin) return Promise.resolve(false);

    var shape = o.shape || this.shape;

    function apply() {
      if (!self.spin) return false;
      var changed = shape !== self.spin.garment;
      self.shape = shape;
      self.spin.garment = shape;
      if (o.marks) self.marks = o.marks;
      /* setColours and refreshMesh each draw, so the order is: colour first,
         geometry second, texture last — the last call is the one that lands
         on screen, and it is the one that carries the new design. */
      if (o.body || o.trim) {
        self.spin.setColours(o.body || self.d.body,
                             o.trim || o.body || self.d.trim || self.d.body);
      }
      if (changed) self.spin.refreshMesh();
      self.spin.refreshTexture();
      return true;
    }

    var url = this.meshes[shape];
    var need = !!(url && window.GarmentSpin && window.GarmentSpin.loadMesh &&
                  !window.GarmentSpin.hasMesh(shape));
    if (!need) return Promise.resolve(apply());
    return this.prefetch(shape).then(apply)['catch'](apply);
  };

  /* Only now is there anything to pause. A reel off screen is a rAF loop and
     a GPU doing work nobody is looking at. */
  Reel.prototype.track = function () {
    var self = this;
    if (!window.IntersectionObserver) { this.visible = true; return; }
    var io = new IntersectionObserver(function (rows) {
      for (var i = 0; i < rows.length; i++) {
        self.visible = rows[i].isIntersecting;
      }
      self.sync();
    }, { threshold: 0.05 });
    io.observe(this.root);
  };

  Reel.prototype.bindButton = function () {
    var self = this;
    if (!this.button) return;
    this.button.addEventListener('click', function () {
      if (self.retired) { self.retired = false; self.paused = false; }
      else self.paused = !self.paused;
      self.sync();
    });
  };

  Reel.prototype.retire = function () {
    this.retired = true;
    this.sync();
  };

  /* One place decides whether the reel is turning, so the button's label,
     the data attribute and the rAF loop cannot disagree about it. */
  Reel.prototype.sync = function () {
    var want = !!this.spin && this.visible && !this.paused &&
               !this.retired && !reduced();
    if (want && !this.running) { this.running = true; this._last = 0; this.step(); }
    if (!want && this.running) {
      this.running = false;
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    if (this.button) {
      var off = this.paused || this.retired;
      this.button.setAttribute('aria-pressed', off ? 'false' : 'true');
      this.button.dataset.epgReelOn = off ? '0' : '1';
      var label = off ? (this.d.labelPlay || 'Spin the garment')
                      : (this.d.labelPause || 'Pause');
      this.button.setAttribute('aria-label', label);
      var txt = this.button.querySelector('[data-epg-reel-label]');
      if (txt) txt.textContent = label;
    }
  };

  /* PER FRAME, WITH THE DELTA CLAMPED — not from a wall clock. A hidden tab
     is served no frames, so a clock-driven yaw banks the whole hidden
     interval and snaps the garment round the moment the tab comes back. */
  Reel.prototype.step = function () {
    var self = this;
    function frame(t) {
      if (!self.running || !self.spin) return;
      var dt = self._last ? Math.min(0.33, (t - self._last) / 1000) : 0;
      self._last = t;
      self.spin.yaw = (self.spin.yaw + self.speed * dt) % TAU;
      self.spin.draw();
      self._raf = requestAnimationFrame(frame);
    }
    this._raf = requestAnimationFrame(frame);
  };

  /* ------------------------------------------------------------ the design

     A reduced `paint3d`. It does not go near zones, metaobjects or
     millimetres — a landing page has no product and therefore no
     personalisation rule to read — so a mark here is authored directly
     against its panel: `u`/`v` are panel-local fractions and `size` is a
     fraction of the panel's WIDTH, which is the unit the designer's own
     `m.size` is in.

     Two things it DOES share with the designer, because getting either wrong
     is visible at a glance:

     - THE SCRATCH CANVAS IS THE PANEL'S OWN SHAPE, never a square. A square
       contain-fitted into a 0.46 x 0.72 panel covers the middle 64% of its
       height and clips anything past the ends, and on `lower` (0.46 x 0.20)
       it draws every mark 2.3x too small.

     - THE MARK IS PRE-STRETCHED BY `inkAspect`, so it lands square on the
       cloth. The panel's proportions are not the garment's: on a pair of
       shorts an uncorrected badge arrives about 1.6x wide.
     -------------------------------------------------------------------- */
  Reel.prototype.paint = function (ctx, size, engine) {
    var self = this;
    var P = window.GarmentSpin.PANELS;
    var byPanel = {};

    this.marks.forEach(function (m) {
      var name = m.panel || 'front';
      if (!P[name]) return;
      (byPanel[name] = byPanel[name] || []).push(m);
    });

    Object.keys(byPanel).forEach(function (name) {
      var panel = P[name];
      var pxw = Math.max(1, Math.round(size * panel.w));
      var pxh = Math.max(1, Math.round(size * panel.h));
      var off = document.createElement('canvas');
      off.width = pxw;
      off.height = pxh;
      var octx = off.getContext('2d');

      byPanel[name].forEach(function (m) {
        var u = m.u == null ? 0.5 : Number(m.u);
        var v = m.v == null ? 0.3 : Number(m.v);
        var cx = u * pxw, cy = v * pxh;
        /* The correction is a measured field over the panel rather than one
           number for it — a beanie's crown and its cuff differ by more than
           4x — so the lookup is told where on the panel this mark is in BOTH
           directions. */
        var ink = engine && engine.inkAspect
          ? engine.inkAspect(panel, panel.y + v * panel.h, panel.x + u * panel.w)
          : 1;
        var wantW = (Number(m.size) || 0.3) * pxw;

        if (m.src) { self.paintImage(octx, m, cx, cy, wantW, ink); return; }

        var text = String(m.value == null ? '' : m.value).toUpperCase();
        if (!text) return;
        var font = m.font || '"Helvetica Neue", Arial, sans-serif';
        /* MEASURE THE FONT SIZE, NOT THE WORD. `size` is a WIDTH but `font`
           takes a HEIGHT, and a word is about 1.4x wider than the type is
           tall — setting the size to the wanted width draws every mark half
           again too big. */
        octx.save();
        octx.font = '800 100px ' + font;
        var w100 = octx.measureText(text).width;
        octx.restore();
        var px = w100 > 0 ? wantW * 100 / w100 : wantW;

        octx.save();
        octx.translate(cx, cy);
        octx.scale(1, ink);
        octx.font = '800 ' + px + 'px ' + font;
        octx.textAlign = 'center';
        octx.textBaseline = 'middle';
        octx.lineJoin = 'round';
        if (m.outline) {
          octx.lineWidth = px * 0.09;
          octx.strokeStyle = m.outline;
          octx.strokeText(text, 0, 0);
        }
        octx.fillStyle = m.color || '#ffffff';
        octx.fillText(text, 0, 0);
        octx.restore();
      });

      /* One to one onto the panel: the scratch canvas IS the panel. */
      ctx.drawImage(off, Math.round(panel.x * size), Math.round(panel.y * size), pxw, pxh);
    });
  };

  /* AN IMAGE HAS NO DIMENSIONS UNTIL IT DECODES, so painting on assignment
     draws nothing and reports no error. The first paint requests it and
     repaints when it lands — the same rule `setFile` carries in the
     designer. Cached images are already `complete`, so they draw on the
     first pass and never queue a second. */
  Reel.prototype.paintImage = function (octx, m, cx, cy, wantW, ink) {
    var self = this;
    var img = this._imgs[m.src];
    if (!img) {
      img = this._imgs[m.src] = new Image();
      img.crossOrigin = 'anonymous';
      img.addEventListener('load', function () {
        if (self.spin) self.spin.refreshTexture();
      });
      img.src = m.src;
    }
    if (!(img.complete && img.naturalWidth)) return;
    var h = wantW * (img.naturalHeight / img.naturalWidth);
    octx.save();
    octx.translate(cx, cy);
    octx.scale(1, ink);
    octx.drawImage(img, -wantW / 2, -h / 2, wantW, h);
    octx.restore();
  };

  /* ---------------------------------------------------------------- start */

  function init() {
    var nodes = document.querySelectorAll('[data-epg-reel]:not([data-epg-reel-bound])');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].dataset.epgReelBound = '1';
      new Reel(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* The theme editor rebuilds a section's markup in place. */
  document.addEventListener('shopify:section:load', init);
  /* A stage that is armed LATER than the page — the workwear nav's featured
     tile, which only becomes a reel the first time its panel is opened —
     binds through this rather than by firing `shopify:section:load`, which
     every other block on the page also listens to. `init` skips anything
     already bound, so calling it twice costs nothing. */
  window.__epgReelInit = init;
}());
